import { useState, useEffect, useRef, useCallback } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { progressApi } from "@api/progressApi";
import type { CompletionStatus } from "@ts/enums/CompletionStatus";
import type { GradeProgressApiResponse } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import { applyLessonStatus } from "./gradeProgressCache";

interface UseGradeProgressReturn {
  progress: GradeProgressApiResponse | null;
  progressStatus: REQUEST_STATUS;
  progressError: ErrorInterface | null;
  forGradeCode: number | undefined;
  isReady: boolean;
  markStale: () => void;
  applyLessonStatusLocally: (
    usageKey: string,
    status: CompletionStatus
  ) => GradeProgressApiResponse | null;
}

export function useGradeProgress(
  gradeCode: number | undefined
): UseGradeProgressReturn {
  const [progress, setProgressState] =
    useState<GradeProgressApiResponse | null>(null);
  const [progressGradeCode, setProgressGradeCode] = useState<
    number | undefined
  >(undefined);
  const [progressStatus, setProgressStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [progressError, setProgressError] = useState<ErrorInterface | null>(
    null
  );

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const [isStale, setIsStale] = useState(false);

  const refetch = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    setIsStale(false);
    setRefetchCounter(c => c + 1);
  }, []);

  // Cross-tab nudge: mark stale without fetching. A stale tab refetches when it
  // next becomes visible, so background tabs do not all refetch at once.
  const markStale = useCallback(() => {
    setIsStale(true);
  }, []);

  useEffect(() => {
    if (!isStale) return;

    // visibilitychange only fires on transitions, so also check immediately in
    // case the tab is already visible when the nudge arrives.
    const refetchIfVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    refetchIfVisible();
    document.addEventListener("visibilitychange", refetchIfVisible);
    return () =>
      document.removeEventListener("visibilitychange", refetchIfVisible);
  }, [isStale, refetch]);

  const progressRef = useRef(progress);
  progressRef.current = progress;

  const applyLessonStatusLocally = useCallback(
    (usageKey: string, status: CompletionStatus) => {
      const { current } = progressRef;
      if (!current) return null;
      const next = applyLessonStatus(current, usageKey, status);
      setProgressState(next);
      return next;
    },
    []
  );

  useEffect(() => {
    if (!gradeCode) {
      setProgressState(null);
      setProgressGradeCode(undefined);
      setProgressStatus(REQUEST_STATUS.IDLE);
      setProgressError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    if (lastFetchedGradeRef.current === gradeCode) return;

    lastFetchedGradeRef.current = gradeCode;
    setProgressStatus(REQUEST_STATUS.PENDING);
    setProgressError(null);

    let cancelled = false;

    progressApi
      .getGradeProgress(gradeCode)
      .then(response => {
        if (cancelled) return;
        setProgressState(response);
        setProgressGradeCode(gradeCode);
        setProgressStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setProgressError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: err?.message ?? "Failed to fetch grade progress"
        });
        setProgressStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;
      });

    return () => {
      cancelled = true;
      lastFetchedGradeRef.current = undefined;
    };
  }, [gradeCode, refetchCounter]);

  const isReady = !!gradeCode && progressGradeCode === gradeCode;

  return {
    progress,
    progressStatus,
    progressError,
    forGradeCode: progressGradeCode,
    isReady,
    markStale,
    applyLessonStatusLocally
  };
}
